import { Box, Typography, alpha } from '@mui/material';
import DropIndicator from './Components/DropIndicator';
import { ReactNode, useRef, useState } from 'react';
import { Node, Path } from 'slate';
import ElementControls from './Components/ElementControls';
import ColumnResizer from './Components/ColumnResizer';
import useHover from './hooks/useHover';
import useSize from './hooks/useSize';
import useOverlay from './hooks/useOverlay';
import useEmailEditor from '@hooks/useEmailEditor';
import { innerBlocks } from '@elements';
import { useTranslation } from 'react-i18next';
import UploadImage from './Components/UploadImage';
import useProperty from '@hooks/useProperty';

const oulineWeight = 1;

const hoverStyle = {
    outlineWidth: 2,
    outlineStyle: 'dashed',
    outlineColor: theme => theme.palette.primary.main,
    outlineOffset: 0,
};

const selectedStyle = {
    outlineWidth: 2,
    outlineStyle: 'solid',
    outlineColor: theme => theme.palette.primary.main,
    outlineOffset: 2,
};

export interface InteractElement {
    children: ReactNode;
    node: Node;
    path: Path;
}

function Container({ children, node, path }: InteractElement) {
    const { draggedElement } = useEmailEditor();
    const { dropPosition, setDropPosition, handleDrop, endDrag } = useOverlay(
        node,
        path,
    );
    const { maxWidth } = node.attributes.style;
    const { getProperty } = useProperty('style', 'attributes', [0, 0]);

    const { paddingTop: marginTop, paddingBottom: marginBottom } =
        getProperty();

    const handleDragOver = (e: DragEvent) => {
        const { top, height } = e.target.getBoundingClientRect();

        const parsedMarginTop = parseInt(marginTop) || 0;
        const y = e.clientY - top - parsedMarginTop;
        const netHeight =
            height - parsedMarginTop - (parseInt(marginBottom) || 0);
        if (y < 1) setDropPosition('top');
        else if (y > netHeight - 1) setDropPosition('bottom');
        else {
            endDrag();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Box
            onDragOver={handleDragOver}
            onDragLeave={endDrag}
            onDrop={handleDrop}
            sx={{
                position: 'absolute',
                width: '100%',
                outlineColor: theme => theme.palette.primary.main,
                outlineStyle: 'solid',
                outlineWidth: 1,
                outlineOffset: oulineWeight,
                pointerEvents: draggedElement ? 'auto' : 'none',
                // pointerEvents: draggedElement === 'section' ? 'auto' : 'none',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    marginTop,
                    marginBottom,
                    maxWidth,
                    mx: 'auto',
                }}
            >
                {children}
                <DropIndicator position={dropPosition} />
            </Box>
        </Box>
    );
}

function Section({ children, node, path }) {
    const { draggedElement } = useEmailEditor();
    const beforeRef = useRef(null);
    const afterRef = useRef(null);
    const { isHovering: isHoveringBefore, isSelected: isSelectedBefore } =
        useHover(node, beforeRef.current);
    const { isHovering: isHoveringAfter, isSelected: isSelectedAfter } =
        useHover(node, afterRef.current);
    const {
        handleDragOver,
        endDrag,
        handleDrop,
        dropPosition,
        isFirst,
        isLast,
    } = useOverlay(node, path);

    const isHovering = isHoveringBefore || isHoveringAfter;
    const isSelected = isSelectedBefore || isSelectedAfter;

    const ref = useRef(null);

    return (
        <Box
            ref={ref}
            onDragOver={handleDragOver}
            onDragLeave={endDrag}
            onDrop={handleDrop}
            sx={{
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: isFirst ? 0 : 2,
                    left: 0,
                    right: 0,
                    bottom: isLast ? 0 : 2,
                    outlineWidth: 0.5,
                    outlineStyle:
                        draggedElement === 'section' ? 'solid' : 'none',
                    outlineColor: theme =>
                        alpha(theme.palette.primary.main, 0.5),
                    outlineOffset: -0.5,
                    pointerEvents: 'none',
                },
                position: 'relative',
                mx: '-60px',
                px: '60px',
                pt: isFirst ? 0 : 0.25,
                pb: isLast ? 0 : 0.25,
                ...(isHovering && { ...hoverStyle }),
                ...(isSelected && { ...selectedStyle }),
            }}
        >
            {isSelected && ref.current && (
                <ElementControls
                    element={node}
                    path={path}
                    anchorEl={ref.current}
                />
            )}
            <Box display="flex">
                <Box
                    ref={beforeRef}
                    sx={{
                        position: 'absolute',
                        height: '100%',
                        left: 0,
                        width: '60px',
                        pointerEvents: 'auto',
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        flex: 1,
                        pointerEvents:
                            draggedElement && draggedElement !== 'section'
                                ? 'auto'
                                : 'none',
                    }}
                >
                    {children}
                    <DropIndicator position={dropPosition} offset={4} />
                </Box>
                <Box
                    ref={afterRef}
                    sx={{
                        position: 'absolute',
                        height: '100%',
                        right: 0,
                        width: '60px',
                        pointerEvents: 'auto',
                    }}
                />
            </Box>
        </Box>
    );
}

function Placeholder({ node, path }) {
    const { t } = useTranslation();
    const { handleDrop } = useOverlay(node, path);
    const [dragOver, setDragOver] = useState(false);

    const handleDragEnter = e => {
        const dataType = e.dataTransfer.types[0];
        const [action, dragedElement] = dataType.split('/');
        if (innerBlocks.includes(dragedElement)) {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
        }
    };

    return (
        <Box
            sx={{
                height: '100px',
                p: 2,
            }}
        >
            <Box
                onDragEnter={handleDragEnter}
                onDragLeave={() => setDragOver(false)}
                onDragOver={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDrop={handleDrop}
                sx={{
                    // color: theme => theme.palette.primary.main,
                    ...(dragOver && {
                        backgroundColor: theme =>
                            alpha(
                                theme.palette.primary.main,
                                theme.palette.action.activatedOpacity,
                            ),
                    }),
                    height: '100%',
                    borderColor: theme => theme.palette.primary.main,
                    borderStyle: 'dashed',
                    borderWidth: 3,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '& > *': {
                        pointerEvents: 'none',
                    },
                }}
            >
                <Typography variant="h6" color="text.secondary" align="center">
                    {t('Drop an element here')}
                </Typography>
            </Box>
        </Box>
    );
}

function Row({ children, node, path }) {
    const {
        gapStart,
        gapEnd,
        handleDrop,
        handleDragOver,
        endDrag,
        dropPosition,
    } = useOverlay(node, path);

    return (
        <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={endDrag}
            sx={{
                position: 'relative',
                display: 'flex',
                '& > *': {
                    flex: 1,
                },
                pt: gapStart + 'px',
                pb: gapEnd + 'px',
                // outline: oulineWeight + 'px solid purple',
                outlineOffset: -oulineWeight / 2,
            }}
        >
            {children}
            <DropIndicator position={dropPosition} />
        </Box>
    );
}

function Column({ children, node, path }) {
    const { i18n } = useTranslation();
    const {
        handleDragOver,
        endDrag,
        handleDrop,
        dropPosition,
        gapStart,
        gapEnd,
        isLast,
    } = useOverlay(node, path);

    const [isDragging, setIsDragging] = useState(false);
    const [width, setWidth] = useState(null);

    const nodeWidth = node.attributes.style.width;
    const maxWidth = isDragging ? width || nodeWidth : nodeWidth;

    return (
        <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={endDrag}
            sx={{
                position: 'relative',
                maxWidth,
                minWidth: maxWidth,
                pl: gapStart + 'px',
                pr: gapEnd + 'px',
                display: 'flex',
                flexDirection: 'column',
                // outline: oulineWeight + 'px solid navy',
                outlineOffset: -oulineWeight / 2,
            }}
        >
            {children}
            <DropIndicator position={dropPosition} />
            {/* <ColumnEndFiller insertNode={columnFillerInsertNode} /> */}
            {!isLast && (
                <ColumnResizer
                    element={node}
                    path={path}
                    isDragging={isDragging}
                    setIsDragging={setIsDragging}
                    setWidth={setWidth}
                    rtl={i18n.dir() === 'rtl'}
                />
            )}
        </Box>
    );
}

function InnerElement({ children, node, path }) {
    const { draggedElement } = useEmailEditor();
    const { isHovering, isSelected } = useHover(node);
    const {
        handleDragOver,
        handleDrop,
        endDrag,
        dropPosition,
        getInnerElementMargins,
        isLast,
    } = useOverlay(node, path);
    const { height } = useSize(node);

    const { innerGapStart, innerGapEnd } = getInnerElementMargins();

    const ref = useRef(null);

    const totalHeight = height - innerGapStart - innerGapEnd;

    const isEmptyImage = node.type === 'image' && !node.attributes.src;

    return (
        <Box
            ref={ref}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={endDrag}
            path={path}
            onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
            }}
            sx={{
                position: 'relative',
                outlineWidth: 1,
                outlineStyle: innerBlocks.includes(draggedElement)
                    ? 'solid'
                    : 'none',
                outlineColor: theme => alpha(theme.palette.primary.main, 0.5),
                outlineOffset: -1,
                minHeight: `${totalHeight}px`,
                ...(isHovering && { ...hoverStyle }),
                ...(isSelected && { ...selectedStyle }),
                ...(isLast && { flex: 1 }),
            }}
        >
            {isSelected && ref.current && (
                <ElementControls
                    element={node}
                    path={path}
                    anchorEl={ref.current}
                />
            )}
            <DropIndicator position={dropPosition} />
            {isEmptyImage && <UploadImage height={height} path={path} />}
        </Box>
    );
}

export { Container, Row, Section, Column, InnerElement, Placeholder };
